// deps
import React, { ReactElement } from "react";

// helpers
import {
    isUPIGroup,
    isNetBankingGroup,
    isCardGroup,
    isPreferredGroup,
    isRewardsGroup,
} from "@payment-client/helpers/PaymentHelper";
import { isOtherPaymentOffersGroup } from "@payment-client/helpers/PaymentOffersHelper";
import { isWalletGroup } from "@payment-client/helpers/WalletHelper";
import { isPayLaterGroup } from "@payment-client/helpers/PaylaterHelper";
import { isSwiggyInstrumentsGroup } from "@payment-client/helpers/SwiggyInstrumentsHelper";
import { isFlipkartSuperCoinsGroup } from "@payment-client/helpers/FlipkartSuperCoinsHelpers";
import { isCODGroup } from "@payment-client/helpers/CodHelper";
import { isCredPayGroup } from "@payment-client/helpers/CredHelper";
import { isInAppUpiGroup } from "@payment-client/helpers/InAppUpiHelper";
import { isCreditCardEMIGroup } from "@payment-client/helpers/CreditCardEMIHelper";
import { isBillToCompanyGroup } from "@payment-client/helpers/BillToCompanyHelper";

// components
import { CODGroupHandler } from "./handlers/CODGroupHandler";
import { UPIGroupHandler } from "./handlers/UPIGroupHandler";
import { InAppUpiGroupHandler } from "./handlers/InAppUpiGroupHandler";
import { WalletGroupHandler } from "./handlers/WalletGroupHandler";
import { NetBankingGroupHandler } from "./handlers/NetBankingGroupHandler";
import { PayLaterGroupHandler } from "./handlers/PayLaterGroupHandler";
import { CardGroupHandler } from "./handlers/CardGroupHandler";
import { PreferredGroupHandler } from "./handlers/PreferredGroupHandler";
import { SwiggyInstrumentsGroupHandler } from "./handlers/SwiggyInstrumentsGroupHandler";
import { CredPayGroupHandler } from "./handlers/CredPayGroupHandler";
import { FlipkartSuperCoinsGroupHandler } from "./handlers/FlipkartSuperCoinsGroupHandler";
import OtherPaymentGroupHandler from "./handlers/OtherPaymentOffersGroupHandler";
import { CreditCardEMIGroupHandler } from "./handlers/CreditCardEMIGroupHandler";
import { RewardsGroupHandler } from "./handlers/RewardsGroupHandler";
import { BillToCompanyGroupHandler } from "./handlers/BillToCompanyGroupHandler";

// interfaces
import { PaymentGroupHandlerProps } from "./interfaces";
import { PaymentMethod, PaymentMethodMeta } from "@swiggy-private/payment-ui";

//contexts
import { PaymentPayContext } from "@payment-client/contexts/PaymentPayContext";

// hooks
import { useRequestForBillImage } from "@payment-client/hooks/UseRequestForBillImage";
import { useBillImageWebview } from "@payment-client/hooks/UseBillImageWebview";

export function PaymentGroupHandler({ paymentGroup, ...props }: PaymentGroupHandlerProps): ReactElement | null {
    const { paymentPageOfferEnabled } = React.useContext(PaymentPayContext);

    const { requestForBillImage } = useRequestForBillImage();
    const { openBillImageWebview } = useBillImageWebview();

    if (!paymentPageOfferEnabled)
        paymentGroup.paymentMethods.forEach(
            (paymentMethod: PaymentMethod<PaymentMethodMeta>) => delete paymentMethod.payableAmount,
        );

    if (isCODGroup(paymentGroup)) {
        return <CODGroupHandler paymentGroup={paymentGroup} {...props} />;
    } else if (isNetBankingGroup(paymentGroup)) {
        return (
            <NetBankingGroupHandler
                paymentGroup={paymentGroup}
                {...props}
                isPreferredGroup={false}
                showMoreBanksList={props.isFrictionPage}
            />
        );
    } else if (isCardGroup(paymentGroup)) {
        return <CardGroupHandler paymentGroup={paymentGroup} {...props} />;
    } else if (isWalletGroup(paymentGroup)) {
        return <WalletGroupHandler paymentGroup={paymentGroup} {...props} />;
    } else if (isPreferredGroup(paymentGroup)) {
        return <PreferredGroupHandler paymentGroup={paymentGroup} {...props} />;
    } else if (isUPIGroup(paymentGroup)) {
        return <UPIGroupHandler paymentGroup={paymentGroup} {...props} />;
    } else if (isPayLaterGroup(paymentGroup)) {
        return <PayLaterGroupHandler paymentGroup={paymentGroup} {...props} />;
    } else if (isSwiggyInstrumentsGroup(paymentGroup)) {
        return <SwiggyInstrumentsGroupHandler paymentGroup={paymentGroup} {...props} />;
    } else if (isFlipkartSuperCoinsGroup(paymentGroup)) {
        return <FlipkartSuperCoinsGroupHandler paymentGroup={paymentGroup} {...props} />; // [TODO] Remove FK coins handler with twid 100% rollout
    } else if (isCredPayGroup(paymentGroup)) {
        return <CredPayGroupHandler paymentGroup={paymentGroup} {...props} />;
    } else if (isOtherPaymentOffersGroup(paymentGroup)) {
        return <OtherPaymentGroupHandler paymentGroup={paymentGroup} {...props} />;
    } else if (isInAppUpiGroup(paymentGroup)) {
        return <InAppUpiGroupHandler paymentGroup={paymentGroup} {...props} />;
    } else if (isCreditCardEMIGroup(paymentGroup)) {
        return <CreditCardEMIGroupHandler paymentGroup={paymentGroup} {...props} />;
    } else if (isRewardsGroup(paymentGroup)) {
        return <RewardsGroupHandler paymentGroup={paymentGroup} {...props} />;
    } else if (isBillToCompanyGroup(paymentGroup)) {
        return (
            <BillToCompanyGroupHandler
                paymentGroup={paymentGroup}
                requestForBillImage={requestForBillImage}
                openBillImageWebview={openBillImageWebview}
                {...props}
            />
        );
    }

    return null;
}
